import { z } from "zod";
import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportCycle,
    AccountingReportRatioType,
} from "@common/constants";
import { ClassFilterType } from "@common/types/class";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    GoogleSheetsScheduleCadence,
    GoogleSheetsScheduleRunOutcome,
    GoogleSheetsScheduleStatus,
} from "@common/types/googleSheetsSchedule";
import { ReportPeriodMode } from "@common/types/reports/period-mode";
import { AccountingReportType } from "@common/types/reports/report-type";
import { SchedulesReportLayout } from "@common/types/reports/schedules-report";
import { JournalEntrySourceFilter } from "../accounting/journalEntry";

export const GoogleSheetsScheduleCadenceSchema = z.nativeEnum(
    GoogleSheetsScheduleCadence,
);

export const GoogleSheetsScheduleStatusSchema = z.nativeEnum(
    GoogleSheetsScheduleStatus,
);

export const GoogleSheetsScheduleRunOutcomeSchema = z.nativeEnum(
    GoogleSheetsScheduleRunOutcome,
);

export const GoogleSheetsScheduleReportImportParamsSchema = z
    .object({
        entityId: z.number().int().positive(),
        reportType: z.nativeEnum(AccountingReportType),
        startDate: z.string(),
        endDate: z.string(),
        isEndDateToday: z.boolean().optional(),
        basis: z.nativeEnum(AccountingLedgerBasis),
        ledgerId: z.string().uuid().optional(),
        classes: z.array(z.string()).optional(),
        classFilterType: z.nativeEnum(ClassFilterType).optional(),
        comparisons: z
            .array(z.nativeEnum(AccountingReportComparison))
            .max(2)
            .optional(),
        comparisonDiffs: z
            .array(z.nativeEnum(AccountingReportComparisonDiff))
            .max(2)
            .optional(),
        counterpartiesIds: z.array(z.string().uuid()).optional(),
        cycle: z.nativeEnum(AccountingReportCycle).optional(),
        periodMode: z.nativeEnum(ReportPeriodMode).optional(),
        ratioTypes: z
            .array(z.nativeEnum(AccountingReportRatioType))
            .max(2)
            .optional(),
        accountCodes: z.array(z.number().int()).optional(),
        journalEntrySources: z
            .array(z.nativeEnum(JournalEntrySourceFilter))
            .optional(),
        minAmount: z.number().optional(),
        maxAmount: z.number().optional(),
        layout: z.nativeEnum(SchedulesReportLayout).optional(),
    })
    .refine(
        (params) =>
            params.isEndDateToday !== true ||
            params.periodMode === ReportPeriodMode.CUSTOM,
        {
            message: "Today is only available for custom date ranges",
            path: ["isEndDateToday"],
        },
    );

export const GoogleSheetsScheduleManagedReportSchema = z.object({
    id: z.string(),
    title: z.string(),
    sheetName: z.string(),
    importParams: GoogleSheetsScheduleReportImportParamsSchema,
});

export const GoogleSheetsScheduleRunReportDetailSchema = z.object({
    reportId: z.string(),
    title: z.string(),
    sheetName: z.string(),
    didSucceed: z.boolean(),
    errorCode: z.string().optional(),
    errorMessage: z.string().optional(),
    durationMs: z.number().optional(),
});

export const GoogleSheetsScheduleMetadataSchema = z.object({
    spreadsheetUrl: z.string().url().optional(),
    sourceDocumentId: z.string().optional(),
    notes: z.string().optional(),
    lastManualRefreshAt: z.string().datetime().optional(),
    consecutiveFailures: z.number().int().nonnegative().optional(),
});

export const GoogleSheetsScheduleSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    spreadsheetId: z.string(),
    spreadsheetTitle: z.string().nullable(),
    cadence: GoogleSheetsScheduleCadenceSchema,
    status: GoogleSheetsScheduleStatusSchema,
    managedReports: z.array(GoogleSheetsScheduleManagedReportSchema),
    metadata: GoogleSheetsScheduleMetadataSchema.nullable(),
    lastRunAt: z.coerce.date().nullable(),
    lastRunOutcome: GoogleSheetsScheduleRunOutcomeSchema.nullable(),
    lastError: z.string().nullable(),
    lastRunDetails: z
        .array(GoogleSheetsScheduleRunReportDetailSchema)
        .nullable(),
    googleSheetsConnectionId: z.string().uuid().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type GoogleSheetsScheduleResponse = z.infer<
    typeof GoogleSheetsScheduleSchema
>;
